import * as React from 'react'
import { useCallback, useEffect, useMemo } from 'react'
import {
  EditableCustomFieldProps,
  ICustomFieldValueFormModel,
} from '../typings'
import { useSelector } from 'react-redux'
import FormHelperText from '@mui/material/FormHelperText'

import { MultiSelect } from '@workwave-tidal/core/components'
import Box from '@mui/material/Box'

import { useFormikContext } from 'formik'

import { useGetError } from '../useGetError'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle'

import {
  aggregateFieldOptions,
  selectAllCustomFieldOptions,
} from '@user/store/customFields'
import { DensityType } from '@user/components/Opportunities/AddOpportunityPage/typings'

type CustomFieldAutocompleteProps = EditableCustomFieldProps & {
  type: 'single' | 'multi'
  selectedOptions: number[]
  whiteBackground?: boolean
}

export function CustomFieldMultiSelect(props: CustomFieldAutocompleteProps) {
  const {
    type,
    selectedOptions,
    field: { id: fieldId, name },
    fieldSetting: { required } = {},
    multiFieldGap,
  } = props

  const tidalThemeActive = useTidalThemeActive()

  const fieldName = `customFields.${type}[${fieldId}]`

  const { hasError, errorText } = useGetError(props)

  const { setFieldValue } = useFormikContext<ICustomFieldValueFormModel>()

  const allOptions = useSelector(selectAllCustomFieldOptions)

  const { fieldOptions, options } = useMemo(() => {
    const fieldOptions = aggregateFieldOptions(fieldId, allOptions)

    return {
      fieldOptions,
      options: fieldOptions?.filter((x) => x.active).map((x) => x.id),
    }
  }, [allOptions, fieldId])

  const getOptionLabel = useCallback(
    (id: number) => fieldOptions.find((x) => x.id === id)?.name ?? '',
    [fieldOptions],
  )

  const getOptionDisabled = useCallback(
    (id: number) => {
      const active = fieldOptions.find((x) => x.id === id)?.active ?? false
      return !active
    },
    [fieldOptions],
  )

  const getId = useCallback(
    (id: number) =>
      fieldOptions.find((x) => x.id === id)?.id?.toString() ?? '-1',
    [fieldOptions],
  )

  useEffect(() => {
    const activeOptions = fieldOptions.filter((x) => x.active)
    if (activeOptions?.length == 1 && required) {
      setFieldValue(
        `${fieldName}.value`,
        activeOptions?.map((row) => row.id),
      )
    }
  }, [fieldOptions, fieldName, required])

  const onChange = useCallback(
    (value: number[]) => {
      setFieldValue(`${fieldName}.value`, value)
    },
    [fieldName, setFieldValue],
  )

  const density: DensityType = tidalThemeActive ? 'normal' : 'compact'

  return (
    <Box my={multiFieldGap ? 3 : undefined}>
      <MultiSelect
        density={density}
        disabled={false}
        required={required}
        getLabel={getOptionLabel}
        getOptionDisabled={getOptionDisabled}
        getId={getId}
        error={hasError}
        items={options}
        onChange={onChange}
        value={selectedOptions}
        label={name}
        optionMode={'normal'}
        inputMode="normal"
        maxSelectedItemsToShow={4}
        maxSelectedItemsToShowOnFocus={4}
        testId={`custom-text-field-${fieldId}`}
      />
      {hasError && (
        <FormHelperText sx={{ ml: 1 }} error>
          {errorText}
        </FormHelperText>
      )}
    </Box>
  )
}
